import * as React from "react";
import { MdOutlineAdd } from "react-icons/md";
import { Link } from "react-router-dom";
import DefaultButton from "../buttons/defaultButton";
import Lottie from "lottie-react";
import NotFoundData from "../../assets/lottie/no-data.json";
import NoSearchDataFound from "../../assets/lottie/no-result.json";
import { FaRegClone } from "react-icons/fa";
import toast from "react-hot-toast";
import { printDateAndTime } from "../../../utils/common";
import ImagePreview from "../ImagePreview";

const stickyTheme = "sticky right-0 bg-gray-50/80 backdrop-blur-sm";
const stickyWhite = "sticky right-0 bg-white";

const Table = React.forwardRef(({ className, wrapperClass, ...props }, ref) => (
  <div
    className={`w-full overflow-x-auto !mt-0 border border-gray-200 rounded-xl ${
      wrapperClass || ""
    }`}
  >
    <table
      ref={ref}
      className={`w-full text-sm bg-white text-left text-gray-600 ${
        className || ""
      }`}
      style={{
        maxWidth: "100%",
      }}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={`bg-gray-50/80 text-gray-500 !max-h-10 !h-10 ${
      className || ""
    }`}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={`divide-y w-full divide-gray-100 ${className || ""}`}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    colSpan={100}
    ref={ref}
    className={`bg-muted font-medium ${className || ""}`}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={`relative min-h-12 h-12 align-middle group transition-colors hover:bg-gray-50/50 ${className || ""}`}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableActionRow = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <td ref={ref} className={`relative ${className || ""}`} {...props}>
      {children ? (
        <div
          className={`absolute top-0 bottom-0 my-auto right-4 h-fit opacity-0 translate-x-4 bg-white
          group-hover:opacity-100 group-hover:translate-x-0
          transition-all duration-300 z-10 flex items-center gap-1.5 px-2 py-1 rounded-lg shadow-sm border border-gray-100`}
        >
          {children}
        </div>
      ) : null}
    </td>
  )
);
TableActionRow.displayName = "TableActionRow";

const TableHead = React.forwardRef(
  ({ className, sticky = false, ...props }, ref) => (
    <th
      ref={ref}
      className={`pl-4 pr-2 font-semibold text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200 h-10 w-auto min-w-auto md:min-w-[140px]
    ${className || ""} ${sticky ? stickyTheme : ""}`}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef(
  (
    {
      className,
      sticky = false,
      variant = "text",
      customCases = null,
      link,
      data,
      meta,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const stickyWhite = "bg-white z-10";
    const handleCopy = () => {
      if (children) {
        navigator.clipboard.writeText(children);
        toast.success("Text Copied!");
      } else {
        toast.error("Nothing to Copy!");
      }
    };

    const renderContent = () => {
      switch (variant) {
        case "link":
          return (
            <Link to={link || "#"} className="text-primary-600 font-medium hover:text-primary-700 transition-colors max-w-60 truncate">
              {children}
            </Link>
          );

          case "onClick":
          return (
            <Link onClick={onClick ? onClick : undefined} className="text-primary-600 font-medium hover:text-primary-700 transition-colors max-w-60 truncate">
              {children}
            </Link>
          );

        case "copy":
          return (
            <span className="flex flex-row gap-1.5 items-center relative max-w-60 truncate">
              {children}
              <FaRegClone
                title="Copy Text"
                className="min-w-4 cursor-pointer text-gray-400 hover:text-primary-600 transition-colors"
                onClick={() => handleCopy()}
              />
            </span>
          );

        case "date":
          return (
            <span className="flex flex-row gap-1 items-center relative text-gray-600 max-w-60 truncate">
              {printDateAndTime({ children })}
            </span>
          );

        case "number":
          return (
            <span className="flex flex-row gap-1 items-center relative text-gray-700 font-medium tabular-nums max-w-60 truncate">
              {children}
            </span>
          );

        case "date-time":
          return (
            <span className="flex flex-row gap-1 items-center relative text-gray-600 max-w-60 truncate">
              {printDateAndTime(children, true)}
            </span>
          );

        case "email":
          return (
            <span className="flex flex-row gap-1 items-center relative text-gray-600 max-w-60 truncate">
              {children}
            </span>
          );

        case "name":
          return (
            <span className="font-semibold items-center relative text-gray-800 max-w-60 truncate">
              {children}
            </span>
          );

        case "select":
          return (
            <span className="items-center relative text-gray-600">
              {children?.name || children || "N/A"}
            </span>
          );

        case "checkbox":
          return (
            <span className="flex flex-row gap-1 items-center relative text-gray-600">
              {children}
            </span>
          );


        case "timeline": {
          const checkIn = data?.checkIn;
          const checkOut = data?.checkOut;
          const isPresent = checkIn && checkOut;

          return (
            <div className="flex items-center justify-center w-full">
              <div className="flex items-center gap-2 w-full">
                <span
                  className={`w-2 h-2 rounded-full ${
                    checkIn ? "bg-green-500" : "bg-red-400"
                  }`}
                />
                <div
                  className={`h-[2px] flex-1 rounded-full ${
                    isPresent ? "bg-green-400" : "bg-red-300"
                  }`}
                />
                <span
                  className={`w-2 h-2 rounded-full ${
                    checkOut ? "bg-green-500" : "bg-red-400"
                  }`}
                />
              </div>
            </div>
          );
        }

        case "status-dot": {
          return (
            <div className="flex items-center gap-1.5 max-w-60 truncate">
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  meta?.color === "green" ? "bg-green-500" : "bg-red-400"
                }`}
              />
              <span className="text-gray-700">{data}</span>
            </div>
          );
        }


        case "profile": {
          const profile = children || {};
          const getInitials = (name) => {
            if (!name) return "";
            const words = name.trim().split(" ");
            return words
              .slice(0, 2)
              .map((w) => w[0])
              .join("")
              .toUpperCase();
          };

          const ProfileContent = (
            <div className="flex items-center gap-2 max-w-60 truncate">
              {profile.image ? (
                <img
                  src={profile.image}
                  alt="profile"
                  className="w-9 h-9 p-4 rounded-full object-cover ring-2 ring-gray-100"
                />
              ) : (
                <div className="min-w-9 min-h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 overflow-hidden font-semibold border-2 border-primary-100">
                  {getInitials(profile.name)}
                </div>
              )}
              <div className="flex flex-col">
                <p className="font-semibold text-gray-800 text-sm">
                  {profile.name}
                </p>
                <p className="text-xs text-gray-400 lowercase flex flex-row gap-1">
                 {profile.id}
                </p>
              </div>
            </div>
          );

          return profile.link ? (
            <Link
              to={profile.link}
              className="flex items-center gap-2 text-theme max-w-60 truncate hover:opacity-80 transition-opacity"
            >
              {ProfileContent}
            </Link>
          ) : (
            ProfileContent
          );
        }

        case "status": {
          const defaultCases = {
            0: {
              label: "Pending",
              color: "text-amber-600 text-sm",
              bg: "bg-amber-500",
              pillBg: "bg-amber-50",
            },
            1: {
              label: "Approved",
              color: "text-green-600 text-sm",
              bg: "bg-green-500",
              pillBg: "bg-green-50",
            },
            2: {
              label: "Rejected",
              color: "text-red-500 text-sm",
              bg: "bg-red-500",
              pillBg: "bg-red-50",
            },
            3: {
              label: "Terminated",
              color: "text-red-500 text-sm",
              bg: "bg-red-500",
              pillBg: "bg-red-50",
            },
            4: {
              label: "Inactive",
              color: "text-gray-500 text-sm",
              bg: "bg-gray-400",
              pillBg: "bg-gray-50",
            },
          };
          const value = data;
          const defaultCase = defaultCases[value] || defaultCases[0];
          const custom = (customCases || {})[value] || {};
          const label = custom.label || defaultCase.label;
          const colorClass = custom.color || defaultCase.color;
          const bgColor = custom.bg || defaultCase.bg;
          const pillBg = custom.pillBg || defaultCase.pillBg || "bg-gray-50";
          return (
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${pillBg} max-w-60 truncate`}>
              <span className={`w-1.5 h-1.5 rounded-full ${bgColor}`}></span>
              <span
                className={`flex flex-row gap-1 items-center text-xs font-medium ${colorClass}`}
              >
                {label}
              </span>
            </div>
          );
        }

        case "input": {
          const inputProps = data?.inputProps || {};
          return (
            <input
              {...inputProps}
              className={`w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white
                focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-400
                transition-all duration-200 hover:border-gray-300 ${inputProps.className || ''}`}
            />
          );
        }

        case "image": {
          return (
            <ImagePreview
              data={data}
              maxVisible={3}
              imageSize={{ width: 32, height: 32 }}
              placeholder="-"
            />
          );
        }

        case "text":
        default:
          return <span className="text-gray-600 max-w-60 truncate">{children}</span>;
      }
    };

    return (
      <td
        ref={ref}
        className={`py-2.5 font-normal text-gray-700 pl-4 pr-2 max-w-60 truncate ${className || ""} ${
          sticky ? stickyWhite : ""
        }`}
        {...props}
      >
        {renderContent()}
      </td>
    );
  }
);
TableCell.displayName = "TableCell";

const LinkTableCell = React.forwardRef(
  ({ className, link = "#", children, ...props }, ref) => (
    <td
      ref={ref}
      className={`h-12 font-normal text-gray-700 pl-4 ${className || ""}`}
      {...props}
    >
      <Link to={link} className="hover:text-primary-600 font-medium cursor-pointer transition-colors">
        {children}
      </Link>
    </td>
  )
);
LinkTableCell.displayName = "LinkTableCell";

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption ref={ref} className={` ${className || ""}`} {...props} />
));
TableCaption.displayName = "TableCaption";

const NoDataFound = ({
  text = "No Data Found!",
  colSpan = 4,
  className = "",
  search,
  error = false,
  addButton = null,
  addHandler = () => null,
  asTableCell = true,
}) => {
  const content = (
    <div className="flex flex-col justify-center items-center gap-3 py-6">
      {search ? (
        <div className="pb-2 flex flex-col items-center">
          <Lottie
            animationData={NoSearchDataFound}
            loop={true}
            className="h-[100px]"
          />
          <span className="text-gray-500 text-sm mt-2">No Search Result Found!</span>
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center">
          <Lottie
            animationData={NotFoundData}
            loop={true}
            className="h-[100px]"
          />
          <div className="max-w-[550px] text-center mt-2">
            <p className="text-gray-500 text-sm">{text}</p>
          </div>
        </div>
      )}
      {addButton && (
        <DefaultButton
          variant="default"
          icon={<MdOutlineAdd />}
          name={addButton}
          onClick={addHandler}
        />
      )}
    </div>
  );

  if (asTableCell) {
    return (
      <td
        colSpan={colSpan}
        className={`h-40 text-center font-normal ${
          error ? "text-red-500" : "text-gray-600"
        } pl-4 ${className || ""}`}
      >
        {content}
      </td>
    );
  }

  // Return as div when not used in table context
  return (
    <div
      className={`h-40 text-center font-normal flex items-center justify-center ${
        error ? "text-red-500" : "text-gray-600"
      } ${className || ""}`}
    >
      {content}
    </div>
  );
};



export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableActionRow,
  TableCell,
  TableCaption,
  LinkTableCell,
  NoDataFound,
};
